/**
 * Утилиты для работы с бинарными QR-токенами
 * 
 * V1 формат (75 байт) - legacy:
 * - employee_short_id: 4 байта (uint32)
 * - timestamp: 4 байта (unix timestamp в секундах)
 * - role: 1 байт (0 = worker, 1 = admin)
 * - nonce: 2 байта (uint16)
 * - signature: 64 байта (Ed25519 signature)
 * 
 * V2 формат (73 байт) - optimized:
 * - employee_short_id: 3 байта (uint24, до 16.7 млн)
 * - timestamp_rel: 3 байта (uint24, секунды с 01.01.2025)
 * - role_flags: 1 байт (битовая маска: bit 0 = role, остальные резерв)
 * - nonce: 2 байта (uint16)
 * - signature: 64 байта (Ed25519 signature)
 */

import nacl from 'tweetnacl'

// Константы для форматов
export const BINARY_TOKEN_SIZE_V1 = 75 // bytes (legacy)
export const BINARY_TOKEN_SIZE_V2 = 73 // bytes (optimized: 3+3+1+2+64)
export const BINARY_TOKEN_SIZE = BINARY_TOKEN_SIZE_V2 // используем новый формат по умолчанию

// Epoch для относительного timestamp (01.01.2025 00:00:00 UTC)
export const EPOCH_2025 = 1735689600

export interface BinaryTokenPayload {
  employee_short_id: number // uint32
  timestamp: number // unix timestamp (seconds)
  role: 'worker' | 'admin'
  nonce: number // uint16
}

export interface BinaryToken {
  payload: BinaryTokenPayload
  signature: Uint8Array // 64 bytes
}

/**
 * Преобразует hex строку в Uint8Array
 */
export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error('Hex string must have an even length')
  }
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }
  return bytes
}

/**
 * Преобразует Uint8Array в hex строку
 */
export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Утилиты для работы с uint24 (3 байта)
 */
export function setUint24(view: DataView, offset: number, value: number, littleEndian: boolean = true): void {
  if (value < 0 || value > 0xFFFFFF) {
    throw new Error(`Value out of range for uint24: ${value}`)
  }
  if (littleEndian) {
    view.setUint8(offset, value & 0xFF)
    view.setUint8(offset + 1, (value >> 8) & 0xFF)
    view.setUint8(offset + 2, (value >> 16) & 0xFF)
  } else {
    view.setUint8(offset, (value >> 16) & 0xFF)
    view.setUint8(offset + 1, (value >> 8) & 0xFF)
    view.setUint8(offset + 2, value & 0xFF)
  }
}

export function getUint24(view: DataView, offset: number, littleEndian: boolean = true): number {
  if (littleEndian) {
    return view.getUint8(offset) | (view.getUint8(offset + 1) << 8) | (view.getUint8(offset + 2) << 16)
  } else {
    return (view.getUint8(offset) << 16) | (view.getUint8(offset + 1) << 8) | view.getUint8(offset + 2)
  }
}

/**
 * Кодирует payload в бинарный формат V1 (9 байт) - legacy
 */
export function encodePayloadV1(payload: BinaryTokenPayload): Uint8Array {
  const buffer = new Uint8Array(9) // 4 + 4 + 1
  const view = new DataView(buffer.buffer)

  view.setUint32(0, payload.employee_short_id, true) // little-endian
  view.setUint32(4, payload.timestamp, true) // little-endian
  view.setUint8(8, payload.role === 'admin' ? 1 : 0)

  return buffer
}

/**
 * Кодирует payload в бинарный формат V2 (7 байт) - optimized
 */
export function encodePayloadV2(payload: BinaryTokenPayload): Uint8Array {
  const buffer = new Uint8Array(7) // 3 + 3 + 1
  const view = new DataView(buffer.buffer)

  // employee_short_id (3 байта, uint24)
  setUint24(view, 0, payload.employee_short_id, true)

  // timestamp_rel (3 байта, uint24) - относительный timestamp с EPOCH_2025
  const timestamp_rel = payload.timestamp - EPOCH_2025
  if (timestamp_rel < 0 || timestamp_rel > 0xFFFFFF) {
    throw new Error(`Timestamp out of range for uint24: ${timestamp_rel}`)
  }
  setUint24(view, 3, timestamp_rel, true)

  // role_flags (1 байт): bit 0 = role (0=worker, 1=admin), остальные биты резерв
  const roleFlags = payload.role === 'admin' ? 0x01 : 0x00
  view.setUint8(6, roleFlags)

  return buffer
}

/**
 * Кодирует payload в бинарный формат (использует V2 по умолчанию)
 */
export function encodePayload(payload: BinaryTokenPayload): Uint8Array {
  return encodePayloadV2(payload)
}

/**
 * Декодирует payload из бинарного формата V1 (9 байт) - legacy
 */
export function decodePayloadV1(encodedPayload: Uint8Array): BinaryTokenPayload {
  if (encodedPayload.length !== 9) {
    throw new Error(`Invalid V1 payload length: expected 9, got ${encodedPayload.length}`)
  }

  const view = new DataView(encodedPayload.buffer, encodedPayload.byteOffset, encodedPayload.byteLength)

  const employee_short_id = view.getUint32(0, true) // little-endian
  const timestamp = view.getUint32(4, true) // little-endian
  const role = view.getUint8(8) === 1 ? 'admin' : 'worker'

  return { employee_short_id, timestamp, role, nonce: 0 }
}

/**
 * Декодирует payload из бинарного формата V2 (7 байт) - optimized
 */
export function decodePayloadV2(encodedPayload: Uint8Array): BinaryTokenPayload {
  if (encodedPayload.length !== 7) {
    throw new Error(`Invalid V2 payload length: expected 7, got ${encodedPayload.length}`)
  }

  const view = new DataView(encodedPayload.buffer, encodedPayload.byteOffset, encodedPayload.byteLength)

  // employee_short_id (3 байта, uint24)
  const employee_short_id = getUint24(view, 0, true)

  // timestamp_rel (3 байта, uint24) - конвертируем в абсолютный timestamp
  const timestamp_rel = getUint24(view, 3, true)
  const timestamp = EPOCH_2025 + timestamp_rel

  // role_flags (1 байт)
  const roleFlags = view.getUint8(6)
  const role = (roleFlags & 0x01) === 1 ? 'admin' : 'worker'

  return { employee_short_id, timestamp, role, nonce: 0 }
}

/**
 * Декодирует payload из бинарного формата (автоопределение версии)
 */
export function decodePayload(encodedPayload: Uint8Array): BinaryTokenPayload {
  if (encodedPayload.length === 9) {
    return decodePayloadV1(encodedPayload)
  } else if (encodedPayload.length === 7) {
    return decodePayloadV2(encodedPayload)
  } else {
    throw new Error(`Invalid payload length: expected 7 or 9, got ${encodedPayload.length}`)
  }
}

/**
 * Генерирует бинарный токен с подписью
 * @param payload - данные для токена
 * @param privateKeyHex - приватный ключ в hex формате (32 байта)
 * @returns hex-строка токена (75 байт * 2 = 150 символов)
 */
export async function generateBinaryToken(
  payload: BinaryTokenPayload,
  privateKeyHex: string
): Promise<string> {
  // Кодируем payload (9 байт)
  const encodedPayload = encodePayload(payload)

  // Генерируем nonce (2 байта)
  const nonce = new Uint8Array(2)
  crypto.getRandomValues(nonce)

  // Создаем сообщение для подписи: payload + nonce
  const message = new Uint8Array(11) // 9 + 2
  message.set(encodedPayload, 0)
  message.set(nonce, 9)

  // Преобразуем приватный ключ из hex
  const privateKeyBytes = hexToBytes(privateKeyHex)
  
  if (privateKeyBytes.length !== 32) {
    throw new Error(`Invalid private key length: expected 32, got ${privateKeyBytes.length}`)
  }

  // Генерируем keypair из seed (приватный ключ)
  const keyPair = nacl.sign.keyPair.fromSeed(privateKeyBytes)

  // Подписываем сообщение
  const signature = nacl.sign.detached(message, keyPair.secretKey)

  // Собираем финальный токен: payload (9) + nonce (2) + signature (64) = 75 байт
  const fullToken = new Uint8Array(BINARY_TOKEN_SIZE)
  fullToken.set(encodedPayload, 0)
  fullToken.set(nonce, 9)
  fullToken.set(signature, 11)

  return bytesToHex(fullToken)
}

/**
 * Проверяет бинарный токен (поддерживает V1 и V2 форматы)
 * @param tokenHex - токен в hex формате (146 или 150 символов)
 * @param publicKeyHex - публичный ключ в hex формате (32 байта)
 * @returns payload если токен валиден, null если невалиден
 */
export async function verifyBinaryToken(
  tokenHex: string,
  publicKeyHex: string
): Promise<BinaryTokenPayload | null> {
  // Определяем версию токена по длине
  const tokenLength = tokenHex.length / 2
  let isV2 = false
  let payloadSize = 0
  let nonceOffset = 0
  let signatureOffset = 0

  if (tokenLength === BINARY_TOKEN_SIZE_V2) {
    // V2 формат: 73 байта (7 payload + 2 nonce + 64 signature)
    isV2 = true
    payloadSize = 7
    nonceOffset = 7
    signatureOffset = 9
  } else if (tokenLength === BINARY_TOKEN_SIZE_V1) {
    // V1 формат: 75 байт (9 payload + 2 nonce + 64 signature)
    isV2 = false
    payloadSize = 9
    nonceOffset = 9
    signatureOffset = 11
  } else {
    console.error(`[BinaryToken] Invalid token length: expected ${BINARY_TOKEN_SIZE_V1 * 2} or ${BINARY_TOKEN_SIZE_V2 * 2}, got ${tokenHex.length}`)
    return null
  }

  console.log(`[BinaryToken] Verifying token: format=${isV2 ? 'V2' : 'V1'}, length=${tokenLength} bytes`)

  try {
    // Декодируем токен из hex
    const tokenBytes = hexToBytes(tokenHex)

    // Разбиваем токен на части
    const encodedPayload = tokenBytes.slice(0, payloadSize)
    const nonce = tokenBytes.slice(nonceOffset, nonceOffset + 2)
    const signature = tokenBytes.slice(signatureOffset, tokenLength)

    // Создаем сообщение для проверки: payload + nonce
    const message = new Uint8Array(payloadSize + 2)
    message.set(encodedPayload, 0)
    message.set(nonce, payloadSize)

    // Преобразуем публичный ключ из hex
    const publicKeyBytes = hexToBytes(publicKeyHex)

    if (publicKeyBytes.length !== 32) {
      console.error(`[BinaryToken] Invalid public key length: expected 32, got ${publicKeyBytes.length}`)
      return null
    }

    // Проверяем подпись
    const isValid = nacl.sign.detached.verify(message, signature, publicKeyBytes)

    if (!isValid) {
      console.error('[BinaryToken] Signature verification failed')
      return null
    }

    // Декодируем payload (автоопределение версии по длине)
    const payload = decodePayload(encodedPayload)
    
    // Добавляем nonce из токена
    const nonceValue = new DataView(nonce.buffer, nonce.byteOffset, nonce.byteLength).getUint16(0, true)
    payload.nonce = nonceValue

    console.log(`[BinaryToken] Token verified successfully: format=${isV2 ? 'V2' : 'V1'}`)

    return payload
  } catch (error) {
    console.error('[BinaryToken] Error verifying token:', error)
    return null
  }
}

/**
 * Определяет формат токена (JWT или Binary V1/V2)
 */
export class TokenFormatDetector {
  static isBinaryToken(token: string): boolean {
    // Binary token: hex строка длиной 146 (V2) или 150 (V1) символов
    const isV2 = token.length === BINARY_TOKEN_SIZE_V2 * 2 && /^[0-9a-fA-F]+$/.test(token)
    const isV1 = token.length === BINARY_TOKEN_SIZE_V1 * 2 && /^[0-9a-fA-F]+$/.test(token)
    return isV2 || isV1
  }

  static isBinaryTokenV2(token: string): boolean {
    // Binary token V2: hex строка длиной 146 символов (73 байт * 2)
    return token.length === BINARY_TOKEN_SIZE_V2 * 2 && /^[0-9a-fA-F]+$/.test(token)
  }

  static isBinaryTokenV1(token: string): boolean {
    // Binary token V1: hex строка длиной 150 символов (75 байт * 2)
    return token.length === BINARY_TOKEN_SIZE_V1 * 2 && /^[0-9a-fA-F]+$/.test(token)
  }

  static isJWT(token: string): boolean {
    // JWT: три части, разделенные точками
    return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)
  }
}

/**
 * Маппер для преобразования employee_id <-> short_id
 */
export class EmployeeIdMapper {
  /**
   * Получает short_id по employee_id из локального кэша
   */
  static async getShortId(_employeeId: string): Promise<number | null> {
    // Реализация в employeesRepo
    return null
  }

  /**
   * Получает employee_id по short_id из локального кэша
   */
  static async getEmployeeId(_shortId: number): Promise<string | null> {
    // Реализация в employeesRepo
    return null
  }
}

